import { useAgentContext } from '@src/components/agent-layout/agent-layout';
import { useCloudAuthed } from '@src/hooks/use-cloud-authed';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDockNavigation } from '@src/navigation/useDockNavigation';
import {
  ActionType,
  clearPendingAction,
  getPendingAction,
  PendingLoginAction,
  storePendingAction,
} from '../components/login-required-dialog';

interface UseLoginRequiredReturn {
  showLoginDialog: boolean;
  closeLoginDialog: () => void;
  requiresLogin: boolean;
  checkLoginAndProceed: (
    action: ActionType,
    message?: string,
    options?: Record<string, unknown>,
    guardOptions?: LoginGuardOptions,
  ) => boolean;
  pendingAction: PendingLoginAction | null;
  clearPending: () => void;
  isPostLogin: boolean;
}

interface LoginGuardOptions {
  forceLogin?: boolean;
}

export const useLoginRequired = (): UseLoginRequiredReturn => {
  const { agent } = useAgentContext();
  const { navigation, currentDock } = useDockNavigation();
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingLoginAction | null>(null);

  const requiresLogin = agent?.site_config?.feature_flags?.require_login ?? false;
  const returnedFromLogin = !!currentDock?.options?.login || !!currentDock?.options?.signup;
  const loginSatisfied = useCloudAuthed();
  const isPostLogin = returnedFromLogin || Boolean(loginSatisfied && pendingAction);

  // On mount and when returning from login, check for pending action
  useEffect(() => {
    if ((returnedFromLogin || loginSatisfied) && loginSatisfied) {
      const pending = getPendingAction();
      if (pending) {
        setPendingAction(pending);
        setShowLoginDialog(false);
      }
    }
    if (returnedFromLogin) {
      // Drop the return markers so a refresh does not look like a fresh return.
      navigation.setOption('login', null);
      navigation.setOption('signup', null);
    }
  }, [returnedFromLogin, loginSatisfied, navigation]);

  const checkLoginAndProceed = useCallback(
    (
      action: ActionType,
      message?: string,
      options?: Record<string, unknown>,
      guardOptions?: LoginGuardOptions,
    ): boolean => {
      const shouldRequireLogin = guardOptions?.forceLogin || requiresLogin;
      // If user is logged in or login not required, proceed
      if (loginSatisfied || !shouldRequireLogin) {
        return true;
      }

      // Store pending action for after login
      storePendingAction(action, message, options);
      setShowLoginDialog(true);
      return false;
    },
    [loginSatisfied, requiresLogin],
  );

  const closeLoginDialog = useCallback(() => {
    setShowLoginDialog(false);
  }, []);

  const clearPending = useCallback(() => {
    clearPendingAction();
    setPendingAction(null);
  }, []);

  return {
    showLoginDialog,
    closeLoginDialog,
    requiresLogin,
    checkLoginAndProceed,
    pendingAction,
    clearPending,
    isPostLogin,
  };
};

/**
 * Run `resume(pending)` once when the user returns from login with a pending
 * action whose `action` is in `actions`, then clear it. Consolidates the
 * post-login resume effect that consumers used to hand-wire around
 * `useLoginRequired`'s `isPostLogin` / `pendingAction` / `clearPending`.
 */
export const useResumeAfterLogin = (
  actions: ActionType | ActionType[],
  resume: (pending: PendingLoginAction) => void,
): void => {
  const { isPostLogin, pendingAction, clearPending } = useLoginRequired();
  // Keep `resume` in a ref so an unstable inline callback doesn't churn the
  // effect deps; the action guard already makes a re-run a no-op, but the ref
  // keeps it strictly fire-once per pending action.
  const resumeRef = useRef(resume);
  resumeRef.current = resume;
  useEffect(() => {
    if (!isPostLogin || !pendingAction) return;
    const list = Array.isArray(actions) ? actions : [actions];
    if (!list.includes(pendingAction.action)) return;
    resumeRef.current(pendingAction);
    clearPending();
  }, [isPostLogin, pendingAction, clearPending]); // `actions` read via closure
};
